import { useEffect, useState } from 'react';
import axios from 'axios';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { SUBPHASES } from '../../appScreens';
import { PIPELINE } from '../../viewerPipelines';

// APP ANALYTICS, the Admin page's section (Louis 2026-10-26): WHAT ONE DEVICE ACTUALLY DID, ONE PASS
// AT A TIME, whatever manual it had open. Pick a browser, then one of its passes; the table is that pass screen by screen in the order it happened, the app's phases
// and the tutorial's screens alike, with each page the device killed and the one that replaced it.
// The figures are each screen's peak (see screenMemory). No averages: an average of an iPhone that
// copes and one that does not describes neither.
type Device = { device: string; name: string | null; userAgent: string; standalone: boolean; runs: number; lastAt: string | null };
type Run = { run: string; startAt: string | null; screens: number; killed: number; lost: number };
type Row = {
  visit: string; kind: 'app' | 'tutorial'; screen: string; ord: number; views: number;
  geometryMb: number; pageMb: number; texturesMb: number; lost: number; killed: boolean; at: string | null;
  keptMb: number; filesMb: number; createdViews: number; awaitingViews: number; awaitingMb: number;
};

const CELL = 'border border-slate-200 px-2 py-1.5 text-center align-middle';
const SELECT = 'rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-xs text-slate-700';

// What a browser is, from its user agent: the device, the browser and its version.
function describe(ua: string): string {
  const device = /iPhone/.test(ua) ? 'iPhone' : /iPad/.test(ua) ? 'iPad' : /Android/.test(ua) ? 'Android'
    : /Macintosh/.test(ua) ? 'Mac' : /Windows/.test(ua) ? 'Windows' : '?';
  const version = (re: RegExp) => re.exec(ua)?.[1];
  const browser = version(/CriOS\/(\d+)/) ? `Chrome ${version(/CriOS\/(\d+)/)}`
    : version(/FxiOS\/(\d+)/) ? `Firefox ${version(/FxiOS\/(\d+)/)}`
      : version(/Edg\/(\d+)/) ? `Edge ${version(/Edg\/(\d+)/)}`
        : version(/Chrome\/(\d+)/) ? `Chrome ${version(/Chrome\/(\d+)/)}`
          : version(/Firefox\/(\d+)/) ? `Firefox ${version(/Firefox\/(\d+)/)}`
            : version(/Version\/([\d.]+).*Safari/) ? `Safari ${version(/Version\/([\d.]+)/)}` : '';
  return [device, browser].filter(Boolean).join(', ');
}

const clock = (iso: string | null) => (iso ? new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '');
const day = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString([], { day: 'numeric', month: 'short' }) : '');

export function AppAnalyticsSection() {
  const { t } = useLanguage();
  const [devices, setDevices] = useState<Device[] | null>(null);
  const [device, setDevice] = useState('');
  const [runs, setRuns] = useState<Run[] | null>(null);
  const [run, setRun] = useState('');
  const [rows, setRows] = useState<Row[] | null>(null);
  const [name, setName] = useState('');
  // A READ THAT FAILED SAYS SO, rather than drawing an empty list that reads as "nothing measured".
  const [failed, setFailed] = useState(false);
  const fail = () => setFailed(true);

  // Read when the section is opened: it mounts only then (see NoticeAdmin).
  useEffect(() => {
    let alive = true;
    axios.get('/api/notice/admin/memory/devices')
      .then((r) => { if (alive) setDevices(r.data.devices); })
      .catch(() => { if (alive) fail(); });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    setRuns(null);
    setRun('');
    if (!device) return;
    let alive = true;
    axios.get('/api/notice/admin/memory/runs', { params: { device } })
      .then((r) => { if (alive) setRuns(r.data.runs); })
      .catch(() => { if (alive) fail(); });
    return () => { alive = false; };
  }, [device]);

  useEffect(() => {
    setRows(null);
    if (!run) return;
    let alive = true;
    axios.get('/api/notice/admin/memory/run', { params: { run } })
      .then((r) => { if (alive) setRows(r.data.rows); })
      .catch(() => { if (alive) fail(); });
    return () => { alive = false; };
  }, [run]);

  const picked = devices?.find((d) => d.device === device);
  useEffect(() => { setName(picked?.name ?? ''); }, [picked?.device, picked?.name]);
  const deviceLabel = (d: Device) => d.name || `${describe(d.userAgent)}${d.standalone ? `, ${t('notice.memory.installed')}` : ''}`;
  const rename = () => {
    if (!picked || name.trim() === (picked.name ?? '')) return;
    void axios.put(`/api/notice/admin/memory/devices/${encodeURIComponent(picked.device)}`, { name })
      .then(() => setDevices((all) => all?.map((d) => (d.device === picked.device ? { ...d, name: name.trim() || null } : d)) ?? all))
      .catch(fail);
  };

  // A screen named as it is shown: the tutorial's as the published page counts them, the app's by
  // its phase and subphase.
  const label = (r: Row) => {
    if (r.kind === 'tutorial') {
      const fixed: Record<string, string> = {
        splash: t('notice.memory.splash'), tools: t('notice.memory.tools'),
        parts: t('notice.memory.parts'), done: t('notice.memory.done'),
      };
      return fixed[r.screen] ?? `${t('notice.memory.step')} ${r.ord - 2}`;
    }
    const [phaseId, sub] = r.screen.split(':');
    const i = PIPELINE.findIndex((p) => p.id === phaseId);
    if (i < 0) return r.screen;
    const subLabel = SUBPHASES[phaseId]?.subs.find((s) => s.id === sub)?.labelKey;
    return `${t('notice.phase')} ${i + 1}${t('notice.step.sep')}${t(PIPELINE[i].labelKey)}${subLabel ? `, ${t(subLabel)}` : ''}`;
  };
  const mb = (n: number) => `${Math.round(n)}`;

  return (
    <section className="w-full rounded-xl border border-slate-200 bg-white p-4">
      <h3 className="text-center text-sm font-bold text-slate-800">{t('notice.memory.title')}</h3>
      <p className="mt-1 text-center text-xs text-slate-500">
        {t('notice.memory.explain')} {t('notice.memory.howCounted')}
      </p>

      <div className="mt-3 flex flex-wrap items-center justify-center gap-2">
        <select className={SELECT} value={device} onChange={(e) => setDevice(e.target.value)}>
          <option value="">{devices && !devices.length ? t('notice.memory.noDevice') : t('notice.memory.pickDevice')}</option>
          {devices?.map((d) => (
            <option key={d.device} value={d.device}>{`${deviceLabel(d)} (${day(d.lastAt)} ${clock(d.lastAt)})`}</option>
          ))}
        </select>
        {picked && (
          <input
            className={`${SELECT} w-44`}
            value={name}
            placeholder={t('notice.memory.namePlaceholder')}
            onChange={(e) => setName(e.target.value)}
            onBlur={rename}
            onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
          />
        )}
        {picked && (
          <select className={SELECT} value={run} onChange={(e) => setRun(e.target.value)}>
            <option value="">{t('notice.memory.pickRun')}</option>
            {runs?.map((r) => (
              <option key={r.run} value={r.run}>
                {`${day(r.startAt)} ${clock(r.startAt)}, ${r.screens} ${t('notice.memory.screens')}, ${r.killed} ${t('notice.memory.deaths')}`}
              </option>
            ))}
          </select>
        )}
      </div>
      {failed && <p className="mt-2 text-center text-xs font-semibold text-red-600">{t('notice.memory.loadFailed')}</p>}

      {rows && (
        <div className="mt-3 overflow-x-auto">
          <table className="w-full border-collapse border border-slate-300 text-xs tabular-nums text-slate-700">
            <thead className="bg-slate-50 text-slate-500">
              <tr>
                <th className={`${CELL} font-semibold`}>{t('notice.memory.time')}</th>
                <th className={`${CELL} font-semibold`}>{t('notice.memory.pageCol')}</th>
                <th className={`${CELL} font-semibold`}>{t('notice.memory.screen')}</th>
                <th className={`${CELL} font-semibold`}>{t('notice.memory.views')}</th>
                <th className={`${CELL} font-semibold`}>{t('notice.memory.total')}</th>
                <th className={`${CELL} font-semibold`}>{t('notice.memory.geometry')}</th>
                <th className={`${CELL} font-semibold`}>{t('notice.memory.page')}</th>
                <th className={`${CELL} font-semibold`}>{t('notice.memory.textures')}</th>
                <th className={`${CELL} font-semibold`}>{t('notice.memory.kept')}</th>
                <th className={`${CELL} font-semibold`}>{t('notice.memory.files')}</th>
                <th className={`${CELL} font-semibold`}>{t('notice.memory.created')}</th>
                <th className={`${CELL} font-semibold`}>{t('notice.memory.awaiting')}</th>
                <th className={`${CELL} font-semibold`}>{t('notice.memory.awaitingMb')}</th>
                <th className={`${CELL} font-semibold`}>{t('notice.memory.lost')}</th>
                <th className={`${CELL} font-semibold`}>{t('notice.memory.killed')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                const before = rows[i - 1];
                // A NEW PAGE: the device loaded one, after the one before was left or killed.
                const fresh = before && before.visit !== r.visit;
                const afterKill = fresh && rows.some((x) => x.visit === before.visit && x.killed);
                return [
                  fresh && (
                    <tr key={`p${i}`} className={afterKill ? 'bg-red-50' : 'bg-slate-50'}>
                      <td className={`${CELL} font-semibold ${afterKill ? 'text-red-600' : 'text-slate-500'}`} colSpan={15}>
                        {t(afterKill ? 'notice.memory.newPageAfterKill' : 'notice.memory.newPage')}
                      </td>
                    </tr>
                  ),
                  <tr key={`${r.visit}:${r.screen}`} className={r.killed ? 'bg-red-50' : ''}>
                    <td className={CELL}>{clock(r.at)}</td>
                    <td className={CELL}>{t(r.kind === 'app' ? 'notice.memory.pageApp' : 'notice.memory.pageTutorial')}</td>
                    <td className={`${CELL} font-medium`}>{label(r)}</td>
                    <td className={CELL}>{r.views}</td>
                    <td className={`${CELL} font-semibold`}>{mb(r.geometryMb + r.pageMb + r.texturesMb)}</td>
                    <td className={CELL}>{mb(r.geometryMb)}</td>
                    <td className={CELL}>{mb(r.pageMb)}</td>
                    <td className={CELL}>{mb(r.texturesMb)}</td>
                    <td className={CELL}>{mb(r.keptMb)}</td>
                    <td className={CELL}>{mb(r.filesMb)}</td>
                    <td className={CELL}>{r.createdViews}</td>
                    <td className={CELL}>{r.awaitingViews}</td>
                    <td className={`${CELL} font-semibold`}>{mb(r.awaitingMb)}</td>
                    <td className={`${CELL} ${r.lost ? 'font-semibold text-red-600' : ''}`}>{r.lost}</td>
                    <td className={`${CELL} ${r.killed ? 'font-semibold text-red-600' : ''}`}>
                      {r.killed ? t('notice.memory.killedHere') : ''}
                    </td>
                  </tr>,
                ];
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
